import React, { useEffect, useState, useCallback } from 'react';
import { StatStrip } from './components/StatStrip';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Users, HardHat, Clock, AlertTriangle, TrendingUp, Wrench, Hammer, CheckCircle2 } from 'lucide-react';

const fmt = (n) => (n || 0).toLocaleString('sv-SE', { maximumFractionDigits: 0 });

const LaborPlanTab = ({ projectId }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [materialPlans, setMaterialPlans] = useState([]);
  const [form, setForm] = useState({ material_plan_id: '', company_markup_pct: 20 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lab, mat] = await Promise.all([
        api.listLaborPlans(projectId),
        api.getMaterialPlans(projectId).catch(() => ({ data: { material_plans: [] } })),
      ]);
      setPlans(lab.data?.plans || []);
      const mps = (mat.data?.material_plans || []).filter(p => p.status === 'completed' && (p.phases || []).length);
      setMaterialPlans(mps);
      if (mps[0]?.id && !form.material_plan_id) setForm(f => ({ ...f, material_plan_id: mps[0].id }));
    } catch (e) {
      toast.error('Kunde inte ladda arbete-planer');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Poll generating plans
  useEffect(() => {
    const genPlans = plans.filter(p => p.status === 'generating');
    if (genPlans.length === 0) return;
    const iv = setInterval(async () => {
      const upd = await Promise.all(genPlans.map(p => api.getLaborPlan(p.id).then(r => r.data).catch(() => null)));
      setPlans(prev => prev.map(p => upd.find(u => u?.id === p.id) || p));
    }, 4000);
    return () => clearInterval(iv);
  }, [plans]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.generateLaborPlan({
        project_id: projectId,
        material_plan_id: form.material_plan_id || null,
        company_markup_pct: parseFloat(form.company_markup_pct) || 0,
        use_project_hourly_rate: true,
      });
      setShowDialog(false);
      toast.success('Arbete-beräkning startad — klar om 1-2 minuter');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte starta beräkning');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="labor-plan-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2"><HardHat size={19} className="text-[#F26B1F]" /> Arbete & Bemanning</h2>
          <p className="text-xs text-muted-foreground mt-0.5">AI allokerar yrken, timmar och maskiner per byggfas. Löner hämtas från dina anställda.</p>
        </div>
        <Button
          onClick={() => setShowDialog(true)}
          className="op-btn-primary !h-[42px]"
          data-testid="btn-generate-labor-plan"
        >
          <HardHat size={14} className="mr-1.5" /> Beräkna arbete
        </Button>
      </div>

      {loading && <Loader2 className="animate-spin text-muted-foreground mx-auto my-8" />}

      {!loading && plans.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          <HardHat size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Ingen arbete-plan än. Klicka "Beräkna arbete" ovan för att AI:n ska allokera bemanning.</p>
        </Card>
      )}

      {(() => {
        const p = plans.find((x) => x.status === 'completed');
        if (!p) return null;
        const k = (n) => `${Math.round(n || 0).toLocaleString('sv-SE')} kr`;
        return <StatStrip testid="labor-stat-strip" subtitle="Steg 2 av 4 · Arbete" title={`${(p.phases || []).length} faser bemannade av AI`}
          stats={[{ label: 'Arbetstimmar', value: `${Math.round(p.total_labor_hours || 0).toLocaleString('sv-SE')} h` }, { label: 'Lönekostnad', value: k(p.total_labor_cost_sek) },
            { label: 'Maskinkostnad', value: k(p.total_machine_cost_sek) }, { label: `Totalt m. påslag ${p.company_markup_pct || 0}%`, value: k(p.total_with_markup_sek), accent: true }]} />;
      })()}

      {plans.map(plan => <LaborPlanCard key={plan.id} plan={plan} onUpdated={load} />)}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="labor-dialog">
          <DialogHeader><DialogTitle>Beräkna arbete med AI</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Bind till materialplan (rekommenderat)</Label>
              <select
                value={form.material_plan_id}
                onChange={(e) => setForm(f => ({ ...f, material_plan_id: e.target.value }))}
                className="w-full h-9 rounded border border-border bg-background px-2 text-sm"
                data-testid="labor-material-plan"
              >
                <option value="">— Ingen (AI bygger egen fas-indelning) —</option>
                {materialPlans.map(mp => (
                  <option key={mp.id} value={mp.id}>
                    {new Date(mp.created_at).toLocaleDateString('sv-SE')} · {(mp.phases || []).length} faser · {mp.job_type}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Om kopplad: AI använder materialplanens faser som struktur för bemanningen.</p>
            </div>
            <div>
              <Label>Företagets påslag / vinst­marginal (%)</Label>
              <Input
                type="number"
                value={form.company_markup_pct}
                onChange={(e) => setForm(f => ({ ...f, company_markup_pct: e.target.value }))}
                data-testid="labor-markup"
              />
              <p className="text-xs text-muted-foreground mt-1">Används för att räkna offert-pris. 0 = ingen marginal, 20 = 20 % påslag.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Avbryt</Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white"
              data-testid="submit-labor-btn"
            >
              {generating ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Startar...</> : <><HardHat size={14} className="mr-1.5" /> Starta beräkning</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LaborPlanCard = ({ plan, onUpdated }) => {
  const [expanded, setExpanded] = useState(false);
  const [markup, setMarkup] = useState(plan.company_markup_pct || 0);

  const saveMarkup = async () => {
    try {
      await api.updateLaborPlan(plan.id, { company_markup_pct: parseFloat(markup) || 0 });
      toast.success('Påslag uppdaterat');
      onUpdated();
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const isGenerating = plan.status === 'generating';
  const isFailed = plan.status === 'failed';

  return (
    <Card className="overflow-hidden" data-testid={`labor-card-${plan.id}`}>
      <button onClick={() => setExpanded(e => !e)} className="w-full p-4 text-left hover:bg-muted/30 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">Arbete­beräkning · {new Date(plan.created_at).toLocaleDateString('sv-SE')}</h4>
            {isGenerating && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Genererar</span>}
            {isFailed && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Fel</span>}
            {plan.status === 'completed' && <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded flex items-center gap-1"><CheckCircle2 size={10} /> Klar</span>}
          </div>
          {!isGenerating && !isFailed && (
            <p className="text-xs text-muted-foreground mt-1">
              {(plan.phases || []).length} faser · {fmt(plan.total_labor_hours)} h · Lön: {fmt(plan.total_labor_cost_sek)} kr · Maskin: {fmt(plan.total_machine_cost_sek)} kr
            </p>
          )}
          {isFailed && <p className="text-xs text-red-600 mt-1">{plan.error_message}</p>}
        </div>
        <div className="text-right">
          {!isGenerating && !isFailed && (
            <>
              <div className="text-xs text-muted-foreground">Total m. påslag {plan.company_markup_pct}%</div>
              <div className="text-lg font-bold text-[hsl(16,100%,55%)]">{fmt(plan.total_with_markup_sek)} kr</div>
            </>
          )}
        </div>
      </button>

      {expanded && !isGenerating && !isFailed && (
        <div className="border-t border-border p-4 space-y-4 bg-muted/10">
          {plan.ai_summary && (
            <div className="bg-background rounded p-3">
              <p className="text-xs font-semibold mb-1">AI översikt</p>
              <p className="text-xs text-muted-foreground">{plan.ai_summary}</p>
            </div>
          )}

          {/* Faser */}
          {(plan.phases || []).map((ph, idx) => (
            <div key={idx} className="bg-background rounded p-3 border border-border" data-testid={`labor-phase-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="bg-[hsl(16,100%,55%)] text-white text-xs font-bold rounded w-6 h-6 flex items-center justify-center">{ph.order}</span>
                  <h5 className="font-semibold text-sm">{ph.name}</h5>
                  {ph.duration_days && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={10} /> {ph.duration_days}d</span>}
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Fas-kostnad</div>
                  <div className="text-sm font-bold">{fmt(ph.phase_cost_sek)} kr</div>
                </div>
              </div>
              {ph.description && <p className="text-xs text-muted-foreground mb-2">{ph.description}</p>}

              {/* Trades */}
              <div className="space-y-1 mb-2">
                <p className="text-xs font-semibold flex items-center gap-1"><Users size={11} /> Yrken ({(ph.trades || []).length})</p>
                {(ph.trades || []).map((t, ti) => (
                  <div key={ti} className="text-xs flex items-center justify-between py-1 border-b border-dashed border-border/50 last:border-0">
                    <span className="flex-1">
                      <span className="font-medium">{t.role}</span>
                      {' '}· {t.workers_needed}p × {Math.round(t.hours_per_worker)}h = {Math.round(t.total_hours)}h
                      {' '}<span className="text-muted-foreground">({t.rate_source === 'employees' ? 'Egen taxa' : t.rate_source === 'project' ? 'Proj.taxa' : 'Branschsnitt'}: {fmt(t.hourly_rate_sek)} kr/h)</span>
                      {t.critical && <span className="ml-1 text-red-600 text-[10px]">● KRITISK</span>}
                    </span>
                    <span className="font-semibold">{fmt(t.cost_sek)} kr</span>
                  </div>
                ))}
              </div>

              {/* Machines */}
              {(ph.machines || []).length > 0 && (
                <div className="space-y-1 mb-2">
                  <p className="text-xs font-semibold flex items-center gap-1"><Wrench size={11} /> Maskiner</p>
                  {ph.machines.map((m, mi) => (
                    <div key={mi} className="text-xs flex items-center justify-between py-1 border-b border-dashed border-border/50 last:border-0">
                      <span>
                        {m.name_or_type}
                        {m.matched_machine && <span className="text-emerald-600 ml-1">(✓ Din: {m.matched_machine})</span>}
                        {' '}· {Math.round(m.machine_hours)}h × {fmt(m.hourly_rate_sek)} kr/h
                      </span>
                      <span className="font-semibold">{fmt(m.cost_sek)} kr</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Risks */}
              {(ph.phase_risks || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                  <p className="text-xs font-semibold text-amber-800 flex items-center gap-1 mb-1"><AlertTriangle size={10} /> Risker i fasen</p>
                  <ul className="text-xs text-amber-700 list-disc pl-4">
                    {ph.phase_risks.map((r, ri) => <li key={ri}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ))}

          {/* Overall risks */}
          {(plan.overall_risks || []).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-xs font-semibold text-red-800 flex items-center gap-1 mb-1"><AlertTriangle size={11} /> Övergripande risker</p>
              <ul className="text-xs text-red-700 list-disc pl-4">
                {plan.overall_risks.map((r, ri) => <li key={ri}>{r}</li>)}
              </ul>
            </div>
          )}

          {/* Summering + Påslag editor */}
          <div className="bg-background rounded p-3 border border-border">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
              <div><p className="text-muted-foreground">Timmar totalt</p><p className="font-bold">{fmt(plan.total_labor_hours)} h</p></div>
              <div><p className="text-muted-foreground">Löne­kostnad</p><p className="font-bold">{fmt(plan.total_labor_cost_sek)} kr</p></div>
              <div><p className="text-muted-foreground">Maskin­kostnad</p><p className="font-bold">{fmt(plan.total_machine_cost_sek)} kr</p></div>
              <div><p className="text-muted-foreground">Grund­total</p><p className="font-bold">{fmt(plan.total_cost_sek)} kr</p></div>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Påslag / marginal (%)</Label>
                <Input type="number" value={markup} onChange={(e) => setMarkup(e.target.value)} className="h-8" data-testid={`markup-input-${plan.id}`} />
              </div>
              <Button size="sm" onClick={saveMarkup} data-testid={`save-markup-${plan.id}`}>Spara</Button>
              <div className="flex-1 text-right">
                <p className="text-xs text-muted-foreground">Total med påslag</p>
                <p className="text-xl font-bold text-[hsl(16,100%,55%)] flex items-center justify-end gap-1"><TrendingUp size={16} /> {fmt(plan.total_with_markup_sek)} kr</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default LaborPlanTab;
